import { throttle } from '../utils/utilities';

const SELECTORS = {
    searchForm: '#header-search-form',
    searchInput: '[type="search"]',
    searchToggle: '[data-action="toggle-search"]',
};

const CLASSES = {
    scrollingDown: 'is-scrolling-down',
    sticky: 'is-sticky',
    searchOpen: 'show',
};

const ATTRS = {
    inert: 'inert',
};

export const Header = (header) => {
    if (!header) return () => {};

    const scrollHandler = initScrollHandler(header);
    const stickyHandler = initStickyHandler(header);
    const searchHandler = initSearchHandler(header);

    return () => {
        scrollHandler.cleanup();
        stickyHandler.cleanup();
        searchHandler.cleanup();
    };
};

/**
 * Search form toggle functionality
 */
const initSearchHandler = (header) => {
    const form = header.querySelector(SELECTORS.searchForm);
    const input = header.querySelector(SELECTORS.searchInput);
    const toggle = header.querySelector(SELECTORS.searchToggle);

    if (!form || !input || !toggle) {
        return { cleanup: () => {} };
    }

    const openSearch = (e) => {
        e.preventDefault();

        if (form.classList.contains(CLASSES.searchOpen)) return;

        form.classList.add(CLASSES.searchOpen);
        form.removeAttribute(ATTRS.inert);
        toggle.style.display = 'none';
        input.focus();

        // Delay adding listener to prevent immediate close
        requestAnimationFrame(() => {
            document.addEventListener('click', handleOutsideClick);
        });
    };

    const closeSearch = () => {
        form.classList.remove(CLASSES.searchOpen);
        form.setAttribute(ATTRS.inert, '');
        toggle.style.display = '';
        input.blur();

        document.removeEventListener('click', handleOutsideClick);
    };

    const handleOutsideClick = (e) => {
        const isClickInside = form.contains(e.target) || toggle.contains(e.target);

        if (!isClickInside) {
            closeSearch();
        }
    };

    const handleEscape = (e) => {
        if (e.key === 'Escape' && form.classList.contains(CLASSES.searchOpen)) {
            closeSearch();
            toggle.focus();
        }
    };

    toggle.addEventListener('click', openSearch);
    document.addEventListener('keydown', handleEscape);

    return {
        cleanup: () => {
            toggle.removeEventListener('click', openSearch);
            document.removeEventListener('click', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
        },
    };
};

/**
 * Toggle scrolling down class based on scroll direction
 */
const initScrollHandler = (header) => {
    let lastScrollY = 0;
    const headerHeight = header.offsetHeight;

    const handleScroll = () => {
        const scrollY = window.scrollY;
        const isScrollingDown = scrollY > lastScrollY;
        const isPastHeader = scrollY >= headerHeight;

        header.classList.toggle(CLASSES.scrollingDown, isScrollingDown && isPastHeader);

        lastScrollY = Math.max(0, scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return {
        cleanup: () => {
            window.removeEventListener('scroll', handleScroll);
        },
    };
};

/**
 * Toggle sticky class when scrolled past top
 */
const initStickyHandler = (header) => {
    const updateSticky = () => {
        header.classList.toggle(CLASSES.sticky, window.scrollY > 0);
    };

    updateSticky();

    const throttledUpdate = throttle(updateSticky, 100);
    window.addEventListener('scroll', throttledUpdate, { passive: true });

    return {
        cleanup: () => {
            window.removeEventListener('scroll', throttledUpdate);
        },
    };
};
